/**
 * OfflineBanner — sticky notice that the browser has lost the network.
 *
 * Listens to the platform `online` / `offline` events and renders a
 * role="status" bar above the main content while offline. Silent when
 * online. Does not try to distinguish "server unreachable" from "no
 * internet" — page-level error states cover server-side failure.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function OfflineBanner() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      className="border-b border-danger/30 bg-danger/10 px-4 py-2 text-center text-sm font-medium text-danger"
    >
      {t('errors.offline')}
    </div>
  );
}
